import React, { useState, useEffect } from 'react';
import { FaFolder, FaFolderOpen, FaFile, FaChevronRight, FaChevronDown, FaHashtag, FaSave, FaEye, FaCog, FaCalendarAlt } from 'react-icons/fa';
import api from '../../services/api';
import toast from '../../utils/toast';
import { clearCustomIdCache } from '../../utils/customIdGenerator';

const CustomIdSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [availableFields, setAvailableFields] = useState({});
  const [expandedNodes, setExpandedNodes] = useState({});
  const [selectedField, setSelectedField] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [formData, setFormData] = useState({
    customIdConfig: {
      enabled: true,
      templates: {
        Forms: {
          template: 'FM{DDMMYYYY}-{UNIQUE_ID}',
          nextSequence: 1,
          prefix: 'FM'
        },
        FormSubmission: {
          template: 'FS{DDMMYYYY}-{UNIQUE_ID}',
          nextSequence: 1,
          prefix: 'FS'
        },
        Invoice: {
          template: 'INV{DDMMYYYY}-{UNIQUE_ID}',
          nextSequence: 1,
          prefix: 'INV'
        },
        MasterData: {
          template: 'MD{DDMMYYYY}-{UNIQUE_ID}',
          nextSequence: 1,
          prefix: 'MD'
        },
        Tasks: {
          template: 'TK{DDMMYYYY}-{UNIQUE_ID}',
          nextSequence: 1,
          prefix: 'TK'
        },
        TrademarkApplication: {
          template: 'TA{DDMMYYYY}-MD{MASTER_DATA_ID}-{UNIQUE_ID}',
          nextSequence: 1,
          prefix: 'TA'
        }
      },
      dateFormat: 'DDMMYYYY',
      separator: '-',
      padding: 4
    }
  });

  useEffect(() => {
    fetchSettings();
    loadAvailableFields();
  }, []);

  const loadAvailableFields = async () => {
    try {
      const response = await api.get('/document-generation/models');
      if (response.data && response.data.data) {
        // Extract fields from the models structure
        const fieldsData = {};
        Object.keys(response.data.data).forEach(modelName => {
          const model = response.data.data[modelName];
          if (model && model.fields) {
            fieldsData[modelName] = model.fields;
          }
        });
        setAvailableFields(fieldsData);
      } else {
        console.warn('No available fields data received');
        setAvailableFields({});
      }
    } catch (error) {
      console.error('Failed to load available fields:', error);
      setAvailableFields({});
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/settings');
      const settings = response.data;
      
      if (settings.customIdConfig) {
        setFormData({
          customIdConfig: {
            enabled: settings.customIdConfig.enabled ?? true,
            templates: {
              Forms: {
                template: settings.customIdConfig.templates?.Forms?.template || 'FM{DDMMYYYY}-{UNIQUE_ID}',
                nextSequence: settings.customIdConfig.templates?.Forms?.nextSequence || 1,
                prefix: settings.customIdConfig.templates?.Forms?.prefix || 'FM'
              },
              FormSubmission: {
                template: settings.customIdConfig.templates?.FormSubmission?.template || 'FS{DDMMYYYY}-{UNIQUE_ID}',
                nextSequence: settings.customIdConfig.templates?.FormSubmission?.nextSequence || 1,
                prefix: settings.customIdConfig.templates?.FormSubmission?.prefix || 'FS'
              },
              Invoice: {
                template: settings.customIdConfig.templates?.Invoice?.template || 'INV{DDMMYYYY}-{UNIQUE_ID}',
                nextSequence: settings.customIdConfig.templates?.Invoice?.nextSequence || 1,
                prefix: settings.customIdConfig.templates?.Invoice?.prefix || 'INV'
              },
              MasterData: {
                template: settings.customIdConfig.templates?.MasterData?.template || 'MD{DDMMYYYY}-{UNIQUE_ID}',
                nextSequence: settings.customIdConfig.templates?.MasterData?.nextSequence || 1,
                prefix: settings.customIdConfig.templates?.MasterData?.prefix || 'MD'
              },
              Tasks: {
                template: settings.customIdConfig.templates?.Tasks?.template || 'TK{DDMMYYYY}-{UNIQUE_ID}',
                nextSequence: settings.customIdConfig.templates?.Tasks?.nextSequence || 1,
                prefix: settings.customIdConfig.templates?.Tasks?.prefix || 'TK'
              },
              TrademarkApplication: {
                template: settings.customIdConfig.templates?.TrademarkApplication?.template || 'TA{DDMMYYYY}-MD{MASTER_DATA_ID}-{UNIQUE_ID}',
                nextSequence: settings.customIdConfig.templates?.TrademarkApplication?.nextSequence || 1,
                prefix: settings.customIdConfig.templates?.TrademarkApplication?.prefix || 'TA'
              }
            },
            dateFormat: settings.customIdConfig.dateFormat || 'DDMMYYYY',
            separator: settings.customIdConfig.separator || '-',
            padding: settings.customIdConfig.padding || 4
          }
        });
      }
    } catch (error) {
      toast.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put('/admin/settings', formData);
      clearCustomIdCache(); // Clear cache so new settings take effect immediately
      toast.success('Custom ID settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleTemplateChange = (collection, field, value) => {
    setFormData(prev => ({
      ...prev,
      customIdConfig: {
        ...prev.customIdConfig,
        templates: {
          ...prev.customIdConfig.templates,
          [collection]: {
            ...prev.customIdConfig.templates[collection],
            [field]: value
          }
        }
      }
    }));
  };

  const toggleNode = (nodePath) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodePath]: !prev[nodePath]
    }));
  };

  const handleFieldSelect = (fieldPath, collection) => {
    setSelectedField(fieldPath);
    setSelectedCollection(collection);
    
    // Insert the selected field into the template
    const currentTemplate = formData.customIdConfig.templates[collection].template;
    const placeholder = `{${fieldPath}}`;
    
    // Replace the cursor position or append to the end
    const newTemplate = currentTemplate + placeholder;
    
    handleTemplateChange(collection, 'template', newTemplate);
  };

  const renderFieldTree = (data, path = '', collection) => {
    if (!data) return null;

    const items = [];
    
    Object.keys(data).forEach(key => {
      const value = data[key];
      const currentPath = path ? `${path}.${key}` : key;
      const isExpanded = expandedNodes[currentPath];
      
      // Simplified logic - just check if it's an object with fields or an array
      let isObject = false;
      let isArray = false;
      
      if (value && typeof value === 'object') {
        if (value.type === 'array' || Array.isArray(value)) {
          isArray = true;
        } else if (value.fields || value.type === 'object') {
          isObject = true;
        }
      }
      
      items.push(
        <div key={currentPath} className="ml-4">
          <div 
            className="flex items-center py-1 hover:bg-gray-100 cursor-pointer"
            onClick={() => {
              if (isObject || isArray) {
                toggleNode(currentPath);
              } else {
                handleFieldSelect(currentPath, collection);
              }
            }}
          >
            <span className="mr-2">
              {isObject || isArray ? (
                isExpanded ? <FaChevronDown className="text-blue-500" /> : <FaChevronRight className="text-blue-500" />
              ) : (
                <FaFile className="text-gray-400" />
              )}
            </span>
            <span className="text-sm">
              {key}
              {isArray && <span className="text-gray-500 text-xs">[]</span>}
            </span>
          </div>
          
          {(isObject || isArray) && isExpanded && (
            <div className="ml-4">
              {isArray ? (
                <div className="text-gray-500 text-xs py-1">
                  Array element - use index like: {currentPath}[0]
                </div>
              ) : (
                renderFieldTree(value.fields || value, currentPath, collection)
              )}
            </div>
          )}
        </div>
      );
    });
    
    return items;
  };

  const generatePreview = (template, collection) => {
    const now = new Date();
    const dateFormat = formData.customIdConfig?.dateFormat || 'DDMMYYYY';
    const padding = formData.customIdConfig?.padding || 4;
    const dateMap = {
      'DDMMYYYY': now.getDate().toString().padStart(2, '0') + 
                  (now.getMonth() + 1).toString().padStart(2, '0') + 
                  now.getFullYear().toString(),
      'MMDDYYYY': (now.getMonth() + 1).toString().padStart(2, '0') + 
                  now.getDate().toString().padStart(2, '0') + 
                  now.getFullYear().toString(),
      'YYYYMMDD': now.getFullYear().toString() + 
                  (now.getMonth() + 1).toString().padStart(2, '0') + 
                  now.getDate().toString().padStart(2, '0'),
      'DD-MM-YYYY': now.getDate().toString().padStart(2, '0') + '-' + 
                    (now.getMonth() + 1).toString().padStart(2, '0') + '-' + 
                    now.getFullYear().toString(),
      'MM-DD-YYYY': (now.getMonth() + 1).toString().padStart(2, '0') + '-' + 
                    now.getDate().toString().padStart(2, '0') + '-' + 
                    now.getFullYear().toString(),
      'YYYY-MM-DD': now.getFullYear().toString() + '-' + 
                    (now.getMonth() + 1).toString().padStart(2, '0') + '-' + 
                    now.getDate().toString().padStart(2, '0')
    };

    let preview = template;
    preview = preview.replace(`{${dateFormat}}`, dateMap[dateFormat]);
    preview = preview.replace('{UNIQUE_ID}', '0001');
    preview = preview.replace('{MASTER_DATA_ID}', '12345');
    preview = preview.replace('{SUB_OBJECT_ID}', '001');

    // Replace any other custom placeholders
    Object.keys(availableFields[collection] || {}).forEach(field => {
      const placeholder = `{${field}}`;
      if (preview.includes(placeholder)) {
        preview = preview.replace(placeholder, `[${field}]`);
      }
    });

    return preview;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <FaCog className="text-blue-500 text-xl" />
            <h2 className="text-2xl font-bold text-gray-900">Custom ID Settings</h2>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center space-x-2 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 disabled:opacity-50"
          >
            <FaSave />
            <span>{saving ? 'Saving...' : 'Save Settings'}</span>
          </button>
        </div>

        {/* Enable/Disable Custom IDs */}
        <div className="mb-6">
          <label className="flex items-center space-x-3">
            <input
              type="checkbox"
              checked={formData.customIdConfig?.enabled ?? true}
              onChange={(e) => handleInputChange('customIdConfig', 'enabled', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-lg font-medium">Enable Custom ID Generation</span>
          </label>
          <p className="text-gray-600 mt-1">When enabled, all new records will get custom IDs based on the templates below.</p>
        </div>

        {/* Global Settings */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FaCalendarAlt className="inline mr-2" />
              Date Format
            </label>
            <select
              value={formData.customIdConfig?.dateFormat || 'DDMMYYYY'}
              onChange={(e) => handleInputChange('customIdConfig', 'dateFormat', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="DDMMYYYY">DDMMYYYY</option>
              <option value="MMDDYYYY">MMDDYYYY</option>
              <option value="YYYYMMDD">YYYYMMDD</option>
              <option value="DD-MM-YYYY">DD-MM-YYYY</option>
              <option value="MM-DD-YYYY">MM-DD-YYYY</option>
              <option value="YYYY-MM-DD">YYYY-MM-DD</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Separator
            </label>
            <input
              type="text"
              value={formData.customIdConfig?.separator || '-'}
              onChange={(e) => handleInputChange('customIdConfig', 'separator', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="-"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FaHashtag className="inline mr-2" />
              ID Padding
            </label>
            <input
              type="number"
              min="1"
              max="10"
              value={formData.customIdConfig?.padding || 4}
              onChange={(e) => handleInputChange('customIdConfig', 'padding', parseInt(e.target.value))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Collection Templates */}
        <div className="space-y-6">
          <h3 className="text-lg font-semibold text-gray-900">Collection Templates</h3>
          
          {formData.customIdConfig?.templates && Object.entries(formData.customIdConfig.templates).map(([collection, template]) => (
            <div key={collection} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-lg font-medium text-gray-900">{collection}</h4>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Available Fields Tree */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Available Fields
                  </label>
                  <div className="border border-gray-300 rounded-lg p-3 h-64 overflow-y-auto bg-gray-50">
                    <div className="text-sm text-gray-600 mb-2">
                      Click on fields to add them to the template
                    </div>
                    {availableFields[collection] ? (
                      renderFieldTree(availableFields[collection], '', collection)
                    ) : (
                      <div className="text-gray-500 text-sm">
                        No fields available for {collection}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Template Editor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Template Pattern
                  </label>
                  <textarea
                    value={template.template}
                    onChange={(e) => handleTemplateChange(collection, 'template', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500 h-32 resize-none"
                    placeholder="e.g., FM{DDMMYYYY}-{UNIQUE_ID}"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Available placeholders: {'{DDMMYYYY}'}, {'{UNIQUE_ID}'}, {'{MASTER_DATA_ID}'}, {'{SUB_OBJECT_ID}'}, and field names from the tree
                  </p>
                </div>
                
                {/* Preview */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preview
                  </label>
                  <div className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 text-gray-700 font-mono h-32 overflow-y-auto">
                    {generatePreview(template.template, collection)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CustomIdSettings; 